import { useEffect } from "react";
import { Link } from "wouter";
import { scrollToPageTop } from "@/components/HelioLogoMark";
import CtaBanner from "@/components/powlo/home/CtaBanner";
import PowloLayout from "@/components/powlo/PowloLayout";
import Seo from "@/components/Seo";
import {
  BAND_TABLE,
  FIXED_PILLARS,
  PILLAR_DETAIL,
  PILLAR_LABELS,
  PILLAR_NICKNAMES,
  WEIGHTS,
  type Pillars,
} from "@/components/powlo/home/exampleScore";

const BAND_COPY: Record<string, string> = {
  Limited: "Solar would struggle to pay for itself here. We will say so, and often recommend doing nothing.",
  Fair: "Workable, but the payback leans heavily on tariff and usage. Expect a smaller system than you were quoted elsewhere.",
  Good: "Worth doing, usually with one pillar holding it back that the design can work around.",
  Strong: "Strong on the pillars that carry the most weight. This is where most well-designed systems land.",
  Excellent: "Near the ceiling for the property. Generation, economics and independence all line up.",
};

const BANDS = BAND_TABLE.map((b) => ({ ...b, body: BAND_COPY[b.label] }));

const DATA_SOURCES = [
  "Ordnance Survey building data",
  "Aerial and satellite imagery",
  "Local irradiance history",
  "Land Registry sold prices",
  "Historic England listings and conservation areas",
  "Live electricity tariff feeds",
  "Current installed pricing from the Powlo installer network",
  "Your own bill or spend band",
];

export default function Methodology() {
  useEffect(() => {
    scrollToPageTop();
  }, []);

  return (
    <PowloLayout>
      <Seo
        title="Home Power Score methodology"
        description="Every property gets a score out of 100 before we design anything. Six pillars, fixed weights, and the reasoning behind every number."
        path="/methodology"
      />
      <section className="flex flex-col gap-4 px-5 pb-8 pt-14 sm:px-8 lg:px-20">
        <div className="text-sm font-bold tracking-widest text-indigo">HOW WE SCORE YOUR HOME</div>
        <h1 className="m-0 max-w-3xl font-display text-4xl font-bold leading-tight tracking-tight sm:text-6xl">
          Home Power Score methodology
        </h1>
        <p className="m-0 max-w-2xl text-lg leading-relaxed text-muted">
          Every property gets a score out of 100 before we design anything. Six pillars, fixed weights, and a
          number that moves as you change the system, the tariff and how you use power. Here is exactly what
          goes into it.
        </p>
        <div className="text-sm text-mutedLight">Powtential™ · Powlo&apos;s scoring engine, v1.0 · methodology v2.0.0 — every change is versioned and dated.</div>
      </section>

      <section className="flex flex-col gap-8 px-5 pb-20 sm:px-8 lg:px-20">
        <h2 className="m-0 font-display text-3xl font-bold tracking-tight sm:text-5xl">The six pillars</h2>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {(Object.keys(PILLAR_LABELS) as (keyof Pillars)[]).map((k) => (
            <div key={k} className="flex flex-col gap-2 rounded-2xl border border-border bg-white p-6">
              <div className="flex items-center justify-between">
                <span className="font-display text-xl font-bold tracking-tight">
                  {PILLAR_LABELS[k]}
                  <span className="block text-xs font-normal text-mutedLight">{PILLAR_NICKNAMES[k]}</span>
                </span>
                <span className="rounded-full bg-[#F1F0EC] px-3 py-1 text-xs font-bold">{Math.round(WEIGHTS[k] * 100)}%</span>
              </div>
              <p className="m-0 text-sm leading-relaxed text-muted">{PILLAR_DETAIL[k]}</p>
              {FIXED_PILLARS.includes(k) && (
                <span className="text-xs font-semibold text-indigo">Fixed — a property fact, never changes.</span>
              )}
            </div>
          ))}
        </div>
        <p className="m-0 max-w-3xl text-base leading-relaxed text-muted">
          The number is a weighted blend of six pillars, each scored independently and none worth the same as
          another. Two homes can both score 75 for completely different reasons — and the design that suits
          them will be completely different too. That is why the breakdown matters more than the headline.
        </p>
      </section>

      <section className="flex flex-col gap-6 px-5 pb-20 sm:px-8 lg:px-20">
        <h2 className="m-0 font-display text-3xl font-bold tracking-tight sm:text-5xl">Reading the score</h2>
        <p className="m-0 max-w-3xl text-base leading-relaxed text-muted">
          A home with nothing fitted typically starts in the low thirties — Limited on the scale below. The
          climb from there is what the plan is for: our own example plan moves from 34 to 73 once the
          recommended system, tariff and usage are applied.
        </p>
        <div className="flex flex-col divide-y divide-border rounded-2xl border border-border bg-white">
          {BANDS.map((b) => (
            <div key={b.label} className="flex flex-col gap-1 p-6 sm:flex-row sm:items-center sm:gap-6">
              <span
                className="inline-flex h-10 w-24 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
                style={{ background: b.color }}
              >
                {b.range}
              </span>
              <div>
                <div className="font-display text-lg font-bold tracking-tight">{b.label}</div>
                <p className="m-0 text-sm leading-relaxed text-muted">{b.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-1 gap-10 px-5 pb-20 sm:px-8 lg:grid-cols-2 lg:px-20">
        <div className="flex flex-col gap-4">
          <h2 className="m-0 font-display text-2xl font-bold tracking-tight">Where the data comes from</h2>
          <ul className="m-0 flex flex-col gap-2 pl-5 text-base leading-relaxed text-muted">
            {DATA_SOURCES.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col gap-4">
          <h2 className="m-0 font-display text-2xl font-bold tracking-tight">What we don&apos;t do</h2>
          <ul className="m-0 flex flex-col gap-2 pl-5 text-base leading-relaxed text-muted">
            <li>
              We do not guarantee savings. Every figure is a model. Weather, occupancy and energy prices move, and
              we say so on every page that carries a number.
            </li>
            <li>
              We do not inflate yields. Our shading allowance starts at 5% and goes up when the imagery justifies
              it. On the example plan we applied 10%, which lowered the numbers.
            </li>
            <li>
              We do not hide a bad score. If your roof scores poorly you will see it, with the reason, and a
              recommendation that may be to do nothing.
            </li>
          </ul>
        </div>
      </section>

      <CtaBanner heading="Score my home." body="Free, in about 60 seconds, with the full six-pillar breakdown and the reasoning behind every number." ctaLabel="Score my home →" ctaHref="/score" />

      <div className="px-5 pb-4 text-center text-sm sm:px-8 lg:px-20">
        <Link href="/score" className="font-bold text-indigo">See the interactive score demo →</Link>
      </div>
    </PowloLayout>
  );
}
